import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import useTokenStore from "../../stores/tokens";
import { compatApi } from "../../utils/Intercepter";
import { useFilteredPagination } from "../../utils/PageUtil";
import { PURPLE, MINT, hexToRgba } from "../../utils/designUtils";
import { useIsMobile } from '../../utils/responsive';

const CATEGORY_LABEL = {
    ALARM: '알림', NOTICE: '공지', SCHEDULE: '일정',
    ORG: '조직', APPROVAL: '결재', PERSONAL: '개인', REPORT: '보고',
};

const CATEGORY_STYLE = {
    ALARM: { c: "#d97757", bg: "rgba(217,119,87,.10)" },
    NOTICE: { c: "#7c6bd9", bg: "rgba(124,107,217,.10)" },
    SCHEDULE: { c: "#5bc2b2", bg: "rgba(91,194,178,.12)" },
    ORG: { c: "#5d8fe0", bg: "rgba(93,143,224,.12)" },
    APPROVAL: { c: "#c44569", bg: "rgba(196,69,105,.10)" },
    PERSONAL: { c: "#9b7cb9", bg: "rgba(155,124,185,.12)" },
    REPORT: { c: "#3a8b8e", bg: "rgba(58,139,142,.12)" },
};

const getSendTypeLabel = (push) => {
    if (push.scheduleType) return '스케줄';
    if (push.resvDt) return '예약';
    return '즉시';
};

const getStatusEntry = (push) => {
    if (push.enabled) return { label: '실행중', color: PURPLE, bg: hexToRgba(PURPLE, 0.12), dot: PURPLE };
    if (!push.scheduleType && push.sendCount > 0) return { label: '발송완료', color: MINT, bg: hexToRgba(MINT, 0.14), dot: MINT };
    return { label: '중지', color: '#c44569', bg: 'rgba(196,69,105,.12)', dot: '#c44569' };
};

const generateCronDescription = (push) => {
    const cron = push.cron;
    if (!cron) return "-";
    const parts = cron.split(' ');
    const minute = parts[1];
    const hour = parseInt(parts[2]);
    const dayOfMonth = parts[3];
    const month = parts[4];
    const dayOfWeek = parts[5];
    const ampm = hour >= 12 ? '오후' : '오전';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    const timeStr = `${ampm} ${hour12}시 ${minute}분`;
    const days = { MON: '월', TUE: '화', WED: '수', THU: '목', FRI: '금', SAT: '토', SUN: '일' };
    const weekMap = { '#1': '첫째', '#2': '둘째', '#3': '셋째', '#4': '넷째' };
    const excludeDays = push.excludedDays || [];
    if (dayOfWeek && dayOfWeek.includes('#')) {
        const [day, week] = dayOfWeek.split('#');
        return month !== '*'
            ? `매년 ${month}월 ${weekMap[`#${week}`] || `${week}번째`} ${days[day] || day} ${timeStr}`
            : `매월 ${weekMap[`#${week}`] || `${week}번째`} ${days[day] || day} ${timeStr}`;
    }
    if (dayOfWeek && dayOfWeek.endsWith('L') && dayOfWeek.length === 4) {
        const day = dayOfWeek.replace('L', '');
        return month !== '*'
            ? `매년 ${month}월 마지막 ${days[day] || day} ${timeStr}`
            : `매월 마지막 ${days[day] || day} ${timeStr}`;
    }
    if (dayOfWeek && dayOfWeek !== '?' && dayOfWeek !== '*')
        return `매주 ${days[dayOfWeek] || dayOfWeek} ${timeStr}`;
    if (month && month !== '*' && dayOfMonth && dayOfMonth !== '*' && dayOfMonth !== '?') {
        const s = `매년 ${month}월 ${dayOfMonth === 'L' ? '말일' : dayOfMonth + '일'} ${timeStr}`;
        return excludeDays.length > 0 ? s + ' (주말 제외)' : s;
    }
    if (dayOfMonth && dayOfMonth !== '*' && dayOfMonth !== '?') {
        const s = `매월 ${dayOfMonth === 'L' ? '말일' : dayOfMonth + '일'} ${timeStr}`;
        return excludeDays.length > 0 ? s + ' (주말 제외)' : s;
    }
    if (excludeDays.length > 0) return `매주 ${timeStr} (${excludeDays.map(d => days[d] || d).join(', ')} 제외)`;
    return `매일 ${timeStr}`;
};

function TypeChip({ label }) {
    const map = {
        '즉시': { color: MINT, bg: hexToRgba(MINT, .14), icon: 'M7 1L2 7h3l-1 4 5-6H6l1-4z' },
        '스케줄': { color: PURPLE, bg: hexToRgba(PURPLE, .12), icon: null },
        '예약': { color: '#d97757', bg: 'rgba(217,119,87,.12)', icon: null },
    };
    const s = map[label] || { color: '#5d5b6e', bg: '#f4f3f9' };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 600, color: s.color, background: s.bg,
            padding: '3px 9px', borderRadius: 6, whiteSpace: 'nowrap',
        }}>
            {label === '즉시' && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><path d={s.icon} fill={s.color} /></svg>}
            {label === '스케줄' && <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke={s.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h6M3 8h6" /><path d="M2 2h8v8H2z" /></svg>}
            {label === '예약' && <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke={s.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="4" /><path d="M6 3.5V6l1.5 1" /></svg>}
            {label}
        </span>
    );
}

function StatusBadge({ entry }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11.5, fontWeight: 600, color: entry.color, background: entry.bg,
            padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
        }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: entry.dot, flexShrink: 0 }} />
            {entry.label}
        </span>
    );
}

function CategoryChip({ code }) {
    if (!code) return <span style={{ color: '#c7c5d2' }}>—</span>;
    const s = CATEGORY_STYLE[code] || { c: '#5d5b6e', bg: '#f4f3f9' };
    return (
        <span style={{
            display: 'inline-flex', fontSize: 11, fontWeight: 600, color: s.c, background: s.bg,
            padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap',
        }}>{CATEGORY_LABEL[code] || code}</span>
    );
}

function StatTile({ label, value, suffix, accent, icon, highlight }) {
    return (
        <div style={{
            background: 'white', borderRadius: 14, padding: '16px 18px',
            border: highlight ? `1.5px solid ${hexToRgba(accent, .35)}` : '1px solid #f0eff5',
            boxShadow: '0 1px 2px rgba(60,50,120,.04)',
            display: 'flex', alignItems: 'center', gap: 14,
        }}>
            <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: hexToRgba(accent, .12), color: accent,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{icon}</div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 500 }}>{label}</span>
                <span style={{ fontSize: 22, fontWeight: 700, color: '#1f1d2e', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                    {value}<span style={{ fontSize: 12, color: '#8b8a99', fontWeight: 500, marginLeft: 3 }}>{suffix}</span>
                </span>
            </div>
        </div>
    );
}

function PillGroup({ label, value, options, onChange }) {
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#8b8a99', fontWeight: 600, letterSpacing: '.04em' }}>{label}</span>
            <div style={{ display: 'inline-flex', gap: 4, background: '#f4f3f9', padding: 3, borderRadius: 10 }}>
                {options.map((opt) => {
                    const active = opt === value;
                    return (
                        <button key={opt} onClick={() => onChange(opt)} style={{
                            height: 28, padding: '0 12px', borderRadius: 7, border: 'none',
                            background: active ? 'white' : 'transparent',
                            color: active ? '#1f1d2e' : '#8b8a99',
                            fontSize: 12, fontWeight: active ? 600 : 500,
                            cursor: 'pointer', fontFamily: 'inherit',
                            boxShadow: active ? '0 1px 3px rgba(60,50,120,.10)' : 'none',
                            transition: 'all 140ms',
                        }}>{opt}</button>
                    );
                })}
            </div>
        </div>
    );
}

export default function TestPushList() {
    const isMobile = useIsMobile(768);
    const { user } = useTokenStore();
    const navigate = useNavigate();

    const [pushList, setPushList] = useState([]);
    const [searchWord, setSearchWord] = useState("");
    const [searchOption, setSearchOption] = useState("all");
    const [filterOpen, setFilterOpen] = useState(false);
    const [typeFilter, setTypeFilter] = useState("전체");
    const [statusFilter, setStatusFilter] = useState("전체");
    const [catFilter, setCatFilter] = useState([]);
    const [loading, setLoading] = useState(true);

    const {
        currentItems, currentPage, totalPages,
        startPage, endPage,
        handlePageChange, handleNextGroup, handlePrevGroup,
        applySort, sortConfig, updateOriginalData,
    } = useFilteredPagination(pushList, null, 10, 5, { key: 'regDt', direction: 'desc' });

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    useEffect(() => {
        async function initialize() {
            try {
                const response = await compatApi.get('/admin/scheduler/find/list?organizationCode=' + (user?.currentOrganizationCode || ''));
                if (response.ok) {
                    const data = response.data.data;
                    const list = data.map((push) => ({ ...push, sendType: getSendTypeLabel(push) }));
                    setPushList(list);
                    updateOriginalData(list);
                } else {
                    alert('푸시 목록을 가져오는 데 실패했습니다.');
                }
            } catch {
                alert('오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        }
        initialize();
    }, [user?.currentOrganizationCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = () => {
        compatApi.post('/admin/scheduler/search', {
            organizationCode: user?.currentOrganizationCode || '',
            searchWord, searchOption,
        }).then(response => {
            if (response.ok) {
                const data = response.data.data;
                const list = data.map(push => ({ ...push, sendType: getSendTypeLabel(push) }));
                handlePageChange(1);
                setPushList(list);
                updateOriginalData(list);
            } else { alert('검색에 실패했습니다.'); }
        }).catch(() => alert('오류가 발생했습니다.'));
    };

    const filteredItems = useMemo(() => {
        return currentItems.filter((p) => {
            const typeLabel = getSendTypeLabel(p);
            if (typeFilter !== '전체' && typeLabel !== typeFilter) return false;
            if (statusFilter !== '전체') {
                const e = getStatusEntry(p);
                if (e.label !== statusFilter) return false;
            }
            if (catFilter.length > 0) {
                const key = p.categoryCode || "NONE";
                if (!catFilter.includes(key)) return false;
            }
            return true;
        });
    }, [currentItems, typeFilter, statusFilter, catFilter]);

    const catCounts = useMemo(() => {
        const c = { TOTAL: 0 };
        const preFiltered = pushList.filter(p => {
            const typeLabel = getSendTypeLabel(p);
            if (typeFilter !== '전체' && typeLabel !== typeFilter) return false;
            if (statusFilter !== '전체') {
                const e = getStatusEntry(p);
                if (e.label !== statusFilter) return false;
            }
            return true;
        });
        preFiltered.forEach(p => {
            const key = p.categoryCode || "NONE";
            c[key] = (c[key] || 0) + 1;
            c.TOTAL++;
        });
        return c;
    }, [pushList, typeFilter, statusFilter]);

    const counts = useMemo(() => {
        const c = { total: pushList.length, sent: 0, scheduled: 0, failed: 0 };
        pushList.forEach((p) => {
            const e = getStatusEntry(p);
            if (e.label === '발송완료') c.sent++;
            else if (e.label === '실행중') c.scheduled++;
            else c.failed++;
        });
        return c;
    }, [pushList]);

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 61px)', background: '#f5f4fa' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${hexToRgba(PURPLE, 0.2)}`, borderTopColor: PURPLE, animation: 'spin 0.7s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const thS = (key) => ({
        padding: '12px 12px', fontSize: 12, fontWeight: 600, color: '#5d5b6e',
        textAlign: 'center', borderBottom: '1px solid #ececf2', whiteSpace: 'nowrap',
        cursor: 'pointer', userSelect: 'none', background: '#f7f6fb', letterSpacing: '.02em',
    });
    const tdS = (align = 'center') => ({
        padding: '11px 12px', textAlign: align,
        borderBottom: '1px solid #f4f3f9', fontSize: 13, color: '#3a3849', whiteSpace: 'nowrap',
    });

    return (
        <div style={{ background: '#f5f4fa', minHeight: 'calc(100vh - 61px)' }}>
            <main style={{ maxWidth: 1600, margin: '0 auto', padding: '32px clamp(20px, 3vw, 40px) 48px' }}>

                {/* Page header */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12, color: '#8b8a99' }}>
                        <span>푸시</span>
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(-90deg)' }}>
                            <path d="M4 6l4 4 4-4" />
                        </svg>
                        <span style={{ color: '#3a3849', fontWeight: 500 }}>푸시 목록</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>푸시 목록</h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#7a7989' }}>발송한 푸시 알림을 조회하고 예약·중지 건을 관리합니다.</p>
                        </div>
                        <button onClick={() => navigate('/admin/push/create')} style={{
                            height: 38, padding: '0 18px', borderRadius: 10,
                            background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                            border: 'none', color: 'white', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                            boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            푸시 추가
                        </button>
                    </div>
                </div>

                {/* Stat tiles */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: 14, marginBottom: 16 }}>
                    <StatTile label="전체 푸시" value={counts.total} suffix="건" accent={PURPLE}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>}
                    />
                    <StatTile label="발송 완료" value={counts.sent} suffix="건" accent={MINT}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>}
                    />
                    <StatTile label="실행 중" value={counts.scheduled} suffix="건" accent={PURPLE}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>}
                    />
                    <StatTile label="중지" value={counts.failed} suffix="건" accent="#c44569" highlight={counts.failed > 0}
                        icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6M15 9l-6 6" /></svg>}
                    />
                </div>

                {/* Filter & Search bar */}
                <div style={{
                    background: 'white', borderRadius: 16, padding: '16px 20px',
                    border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04)',
                    marginBottom: 16,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                        <PillGroup label="발송 타입" value={typeFilter} options={['전체', '즉시', '스케줄', '예약']} onChange={setTypeFilter} />
                        <span style={{ width: 1, height: 22, background: '#ececf2' }} />
                        <PillGroup label="상태" value={statusFilter} options={['전체', '발송완료', '실행중', '중지']} onChange={setStatusFilter} />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
                        <div style={{ position: 'relative' }}>
                            <button onClick={() => setFilterOpen(!filterOpen)} style={{
                                height: 38, padding: '0 30px 0 12px', borderRadius: 10,
                                background: '#fafaff', border: '1.5px solid #ececf2',
                                fontSize: 13, color: '#3a3849', fontWeight: 500,
                                cursor: 'pointer', fontFamily: 'inherit', position: 'relative',
                            }}>
                                {{ all: '전체', title: '푸시 제목', msg_title: '메시지 제목', msg_body: '메시지 내용' }[searchOption] || searchOption}
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#8b8a99" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                                    <path d="M4 6l4 4 4-4" />
                                </svg>
                            </button>
                            {filterOpen && (
                                <>
                                    <div onClick={() => setFilterOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 5 }} />
                                    <div style={{
                                        position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 6,
                                        background: 'white', borderRadius: 10,
                                        boxShadow: '0 8px 24px -8px rgba(60,50,120,.20)',
                                        border: '1px solid #ececf2', minWidth: 130, overflow: 'hidden',
                                    }}>
                                        {[{ val: 'all', label: '전체' }, { val: 'title', label: '푸시 제목' }, { val: 'msg_title', label: '메시지 제목' }, { val: 'msg_body', label: '메시지 내용' }].map(({ val, label }) => (
                                            <button key={val} onClick={() => { setSearchOption(val); setFilterOpen(false); }} style={{
                                                display: 'block', width: '100%', textAlign: 'left',
                                                padding: '9px 14px',
                                                background: searchOption === val ? hexToRgba(PURPLE, .08) : 'white',
                                                border: 'none', color: searchOption === val ? PURPLE : '#3a3849',
                                                fontSize: 13, fontWeight: searchOption === val ? 600 : 500,
                                                cursor: 'pointer', fontFamily: 'inherit',
                                            }}>{label}</button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <input
                            type="text" value={searchWord}
                            onChange={e => setSearchWord(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            placeholder="검색어를 입력하세요"
                            style={{
                                height: 38, width: 280, borderRadius: 10, padding: '0 14px',
                                border: '1.5px solid #ececf2', background: 'white',
                                fontSize: 13, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
                                transition: 'border-color 140ms, box-shadow 140ms',
                            }}
                            onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`; }}
                            onBlur={e => { e.target.style.borderColor = '#ececf2'; e.target.style.boxShadow = 'none'; }}
                        />
                        <button onClick={handleSearch} style={{
                            height: 38, padding: '0 22px', borderRadius: 10, border: 'none',
                            background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                            color: 'white', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                            boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                            </svg>
                            검색
                        </button>
                    </div>

                    {/* Category multi-select chips */}
                    <div style={{
                        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                        paddingTop: 12, borderTop: "1px dashed #ececf2", width: '100%'
                    }}>
                        <span style={{
                            fontSize: 12, color: "#8b8a99", fontWeight: 600, letterSpacing: ".04em",
                            whiteSpace: "nowrap",
                        }}>카테고리</span>
                        <PLCategoryFilter
                            value={catFilter}
                            onChange={setCatFilter}
                            counts={catCounts}
                            purple={PURPLE} mint={MINT}
                        />
                        {catFilter.length > 0 && (
                            <button onClick={() => setCatFilter([])} style={{
                                height: 26, padding: "0 10px", borderRadius: 999,
                                background: "transparent", border: "none",
                                color: "#8b8a99", fontSize: 12, fontWeight: 500,
                                cursor: "pointer", fontFamily: "inherit",
                                display: "inline-flex", alignItems: "center", gap: 4,
                                marginLeft: "auto",
                            }}
                                onMouseEnter={(e) => e.currentTarget.style.color = "#3a3849"}
                                onMouseLeave={(e) => e.currentTarget.style.color = "#8b8a99"}
                            >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                선택 해제 ({catFilter.length})
                            </button>
                        )}
                    </div>
                </div>

                {/* Table card */}
                <div style={{
                    background: 'white', borderRadius: 16,
                    border: '1px solid #f0eff5',
                    boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '16px 20px',
                        background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)',
                        borderBottom: '1px solid #ececf2',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <h2 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e' }}>푸시 목록</h2>
                            <span style={{
                                fontSize: 11, fontWeight: 600, background: 'white', color: '#5d5b6e',
                                padding: '2px 8px', borderRadius: 999, border: '1px solid #ececf2',
                            }}>총 {filteredItems.length}건</span>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 900 }}>
                            <colgroup>
                                <col style={{ width: 72 }} /><col /><col style={{ width: 92 }} /><col style={{ width: 160 }} />
                                <col style={{ width: 110 }} /><col style={{ width: 110 }} /><col style={{ width: 90 }} />
                                <col style={{ width: 90 }} /><col style={{ width: 96 }} />
                            </colgroup>
                            <thead>
                                <tr style={{ background: '#f7f6fb' }}>
                                    {[
                                        ['ID', 'sigId'], ['푸시 제목', 'title'], ['발송 타입', 'sendType'],
                                        ['반복 주기', 'cron'], ['시작일', 'startDt'], ['종료일', 'endDt'],
                                        ['대상자 수', 'segmentCount'], ['카테고리', 'categoryCode'], ['상태', 'enabled'],
                                    ].map(([label, key]) => (
                                        <th key={key} style={thS(key)} onClick={() => applySort(key)}>
                                            {label}{getSortIcon(key)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ padding: '48px 20px', textAlign: 'center', color: '#aaa9b7', fontSize: 13 }}>
                                            조건에 맞는 푸시 알림이 없습니다.
                                        </td>
                                    </tr>
                                ) : filteredItems.map((push) => {
                                    const typeLabel = getSendTypeLabel(push);
                                    const statusEntry = getStatusEntry(push);
                                    return (
                                        <tr key={push.sigId || ''}
                                            style={{ background: 'white', transition: 'background 100ms' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#fafaff'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                        >
                                            <td style={tdS()}>
                                                <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 600, background: '#f4f3f9', padding: '2px 8px', borderRadius: 6 }}>
                                                    #{push.sigId}
                                                </span>
                                            </td>
                                            <td style={tdS('left')} >
                                                <span
                                                    style={{ color: MINT, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}
                                                    onClick={() => navigate('/admin/push/edit', { state: push })}
                                                    onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                                                    onMouseLeave={e => e.target.style.textDecoration = 'none'}
                                                >
                                                    {push.title}
                                                </span>
                                            </td>
                                            <td style={tdS()}><TypeChip label={typeLabel} /></td>
                                            <td style={{ ...tdS('left'), fontSize: 12, color: '#5d5b6e' }}>
                                                {push.scheduleType ? generateCronDescription(push) : <span style={{ color: '#c7c5d2' }}>—</span>}
                                            </td>
                                            <td style={{ ...tdS(), color: push.startDt ? '#5d5b6e' : '#c7c5d2' }}>
                                                {push.startDt ? format(new Date(push.startDt), 'yyyy-MM-dd') : '—'}
                                            </td>
                                            <td style={{ ...tdS(), color: push.endDt ? '#5d5b6e' : '#c7c5d2' }}>
                                                {push.endDt ? format(new Date(push.endDt), 'yyyy-MM-dd') : '—'}
                                            </td>
                                            <td style={tdS()}>
                                                <span style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                                    {push.segmentCount}
                                                    <span style={{ fontSize: 10.5, color: '#aaa9b7', fontWeight: 500, marginLeft: 2 }}>명</span>
                                                </span>
                                            </td>
                                            <td style={tdS()}><CategoryChip code={push.categoryCode} /></td>
                                            <td style={tdS()}><StatusBadge entry={statusEntry} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div style={{
                            padding: '14px 20px', borderTop: '1px solid #ececf2',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#fafaff',
                        }}>
                            <span style={{ fontSize: 12, color: '#8b8a99' }}>
                                {(currentPage - 1) * 10 + 1} - {Math.min(currentPage * 10, pushList.length)} / {pushList.length}
                            </span>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} style={pageBtn(currentPage === 1)}>«</button>
                                <button onClick={handlePrevGroup} disabled={startPage === 1} style={pageBtn(startPage === 1)}>‹</button>
                                {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(n => (
                                    <button key={n} onClick={() => handlePageChange(n)} style={pageBtn(false, n === currentPage)}>{n}</button>
                                ))}
                                <button onClick={handleNextGroup} disabled={endPage >= totalPages} style={pageBtn(endPage >= totalPages)}>›</button>
                                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} style={pageBtn(currentPage === totalPages)}>»</button>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}

function pageBtn(disabled, active = false) {
    return {
        minWidth: 30, height: 30, borderRadius: 7,
        border: active ? 'none' : '1px solid #ececf2',
        background: active ? `linear-gradient(135deg, ${PURPLE}, ${MINT})` : disabled ? '#fafaff' : 'white',
        color: active ? 'white' : disabled ? '#c7c5d2' : '#3a3849',
        fontSize: 12.5, fontWeight: active ? 600 : 400, cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
    };
}

function PLCategoryFilter({ value, onChange, counts, purple, mint }) {
    const CATS = [
        { code: "ALARM", label: "알림", c: "#d97757", bg: "rgba(217,119,87,.10)" },
        { code: "NOTICE", label: "공지", c: "#7c6bd9", bg: "rgba(124,107,217,.10)" },
        { code: "SCHEDULE", label: "일정", c: "#5bc2b2", bg: "rgba(91,194,178,.12)" },
        { code: "ORG", label: "조직", c: "#5d8fe0", bg: "rgba(93,143,224,.12)" },
        { code: "APPROVAL", label: "결재", c: "#c44569", bg: "rgba(196,69,105,.10)" },
        { code: "PERSONAL", label: "개인", c: "#9b7cb9", bg: "rgba(155,124,185,.12)" },
        { code: "REPORT", label: "보고", c: "#3a8b8e", bg: "rgba(58,139,142,.12)" },
        { code: "NONE", label: "미분류", c: "#8b8a99", bg: "#f4f3f9" },
    ];

    const isAll = value.length === 0;
    const toggle = (code) => {
        if (value.includes(code)) onChange(value.filter((v) => v !== code));
        else onChange([...value, code]);
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <button onClick={() => onChange([])} style={{
                height: 28, padding: "0 12px", borderRadius: 999,
                background: isAll ? `linear-gradient(135deg, ${purple}, ${mint})` : "white",
                color: isAll ? "white" : "#5d5b6e",
                fontSize: 12, fontWeight: isAll ? 700 : 600,
                cursor: "pointer", fontFamily: "inherit",
                border: isAll ? "1.5px solid transparent" : "1.5px solid #ececf2",
                boxShadow: isAll ? `0 4px 10px -4px ${hexToRgba(purple, .45)}` : "none",
                display: "inline-flex", alignItems: "center", gap: 6,
                transition: "all 140ms",
            }}>
                전체
                <span style={{
                    fontSize: 10.5, fontWeight: 700,
                    background: isAll ? "rgba(255,255,255,.22)" : "#f4f3f9",
                    color: isAll ? "white" : "#8b8a99",
                    padding: "1px 7px", borderRadius: 999, lineHeight: 1.4,
                    fontVariantNumeric: "tabular-nums",
                }}>{counts.TOTAL || 0}</span>
            </button>

            {CATS.map((c) => {
                const active = value.includes(c.code);
                const n = counts[c.code] || 0;
                const disabled = n === 0 && !active;
                return (
                    <button key={c.code}
                        onClick={() => !disabled && toggle(c.code)}
                        disabled={disabled}
                        style={{
                            height: 28, padding: "0 12px", borderRadius: 999,
                            border: `1.5px solid ${active ? c.c : "#ececf2"}`,
                            background: active ? c.c : (disabled ? "#fafaff" : "white"),
                            color: active ? "white" : (disabled ? "#c7c5d2" : c.c),
                            fontSize: 12, fontWeight: active ? 700 : 600,
                            cursor: disabled ? "not-allowed" : "pointer",
                            fontFamily: "inherit", whiteSpace: "nowrap",
                            display: "inline-flex", alignItems: "center", gap: 6,
                            transition: "all 140ms",
                            opacity: disabled ? .55 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (active || disabled) return;
                            e.currentTarget.style.background = c.bg;
                        }}
                        onMouseLeave={(e) => {
                            if (active || disabled) return;
                            e.currentTarget.style.background = "white";
                        }}
                    >
                        {c.label}
                        <span style={{
                            fontSize: 10.5, fontWeight: 700,
                            background: active ? "rgba(255,255,255,.22)" : (disabled ? "#ececf2" : "#f4f3f9"),
                            color: active ? "white" : (disabled ? "#c7c5d2" : "#8b8a99"),
                            padding: "1px 7px", borderRadius: 999, lineHeight: 1.4,
                            fontVariantNumeric: "tabular-nums",
                        }}>{n}</span>
                    </button>
                );
            })}
        </div>
    );
}
